import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { loginRequest, msalConfig } from './authConfig';
import { useMsal } from "@azure/msal-react";
import './App.css';
import Login from './Components/Login';
import Dashboard from './Components/Dashboard/Dashboard';
import HealthCheck from './Components/Dashboard/HealthCheck';
import Modal from './Components/common/Modal';
import SmsPopup from './Components/common/Modal/SMS/SmsPopup';
import Unzip from './Components/common/Modal/Unzip/Unzip';
import FileUpload from './Components/FileUpload';
import UserList from './Components/User/UserList';
import UserGroup from './Components/User/UserGroup';
import MyHelp from './Components/MyHelp';
import NewFolder from './Components/common/Modal/NewFolder';
import RenameFolder from './Components/common/Modal/RenameFolder';
import BulkUploadPopup from './Components/common/Modal/bulkUploadPopup';
import ErrorBoundary from './Components/common/ErrorBoundary';
import Preview from './Components/common/Modal/Preview';
import DownloadZip from './Components/common/Modal/Preview/DownloadZip';
import Admin from './Components/Admin/AdminPage';
import { extractNonPiiFromIdToken, decodeAccessToken, createCustomOidToken, getAzureTokenId } from './Components/utils/tokenUtils';
import { CLIENT_ID, API_SCOPES } from './Components/utils/Constants';
import tokenRefreshService from './Components/utils/tokenRefreshService';

/**
 * SECURITY: Azure tokens are NOT stored in localStorage.
 * Instead, we create a CUSTOM token with:
 * - oid (object ID)
 * - exp (synced with Azure token expiry)
 * - ati (Azure token unique ID for tracking)
 * - sid (session ID)
 * 
 * This custom token is stored as 'accessToken' in localStorage.
 * The token refresh service silently replaces it ~every hour.
 */

//Root.js


function Root() {
    const navigate = useNavigate();
    const location = useLocation();
    // Use accessToken (custom token) as auth indicator
    const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
    const clientId = localStorage.getItem('clientId');
    let [userReqURL,setUserReqURL] = useState('/mdc/auth/media');
    const { instance, accounts } = useMsal(msalConfig);
    const activeAccount = accounts[0];
    let pathname = location.pathname;

    useEffect(() => {
        const handleClientId = () => {
            if (clientId) {
                // App flow: Check if we have accessToken (custom token set by Login component)
                const storedToken = localStorage.getItem('accessToken');
                if (storedToken) {
                    setAccessToken(storedToken);
                }
                if (location.pathname === '/') {
                    navigate('/mdc/auth/media');
                }
            } else if (activeAccount) {
                const fetchData = async () => {
                    try {
                        await instance.initialize();
                        // ARCHITECTURE: Request backend API token for user authentication
                        const response = await instance.acquireTokenSilent({
                            ...loginRequest,
                            account: activeAccount,
                        });
                        
                        if (!response.accessToken) {
                            throw new Error('Access token not received from MSAL');
                        }
                        
                        // Extract claims from Azure token
                        const decodedAccessToken = decodeAccessToken(response.accessToken);
                        let userOid = null;
                        let userTid = null;
                        let azureExp = null;
                        let azureAti = null;
                        
                        if (decodedAccessToken && decodedAccessToken.oid) {
                            userOid = decodedAccessToken.oid;
                            userTid = decodedAccessToken.tid;
                            azureExp = decodedAccessToken.exp;
                            azureAti = getAzureTokenId(decodedAccessToken);
                        } else {
                            // Fallback: Try to extract from ID token if access token doesn't have oid
                            let idToken = response.idToken;
                            if (!idToken && activeAccount) {
                                // Try to get id_token from MSAL cache
                                for (let i = 0; i < localStorage.length; i++) {
                                    const key = localStorage.key(i);
                                    if (key && key.includes(activeAccount.homeAccountId) && key.includes('idtoken')) {
                                        try {
                                            const tokenData = JSON.parse(localStorage.getItem(key));
                                            if (tokenData?.secret) {
                                                idToken = tokenData.secret;
                                                break;
                                            }
                                        } catch (e) {
                                            // Continue searching
                                        }
                                    }
                                }
                            }
                            
                            if (idToken) {
                                const { oid, tid } = extractNonPiiFromIdToken(idToken);
                                userOid = oid;
                                userTid = tid;
                            } else if (activeAccount?.idTokenClaims) {
                                userOid = activeAccount.idTokenClaims.oid;
                                userTid = activeAccount.idTokenClaims.tid;
                            }
                        }
                        
                        // Create and store custom token (NOT the Azure token)
                        if (userOid) {
                            localStorage.setItem('userOid', userOid);
                            if (userTid) {
                                localStorage.setItem('userTid', userTid);
                            }
                            
                            // Create custom token with Azure token expiry and unique ID
                            const customToken = createCustomOidToken({
                                oid: userOid,
                                azureTokenExp: azureExp,
                                azureTokenId: azureAti,
                                isAppFlow: false,
                                clientId: null
                            });
                            
                            // Store CUSTOM token as accessToken (NOT the Azure token)
                            localStorage.setItem('accessToken', customToken);
                            setAccessToken(customToken);
                        }
                        
                        if (location.pathname === "/") {
                            navigate('/mdc/auth/media');
                        }
                    } catch (error) {
                        let authErrorMessage = "Authentication_error";
                        window.dispatchEvent(new CustomEvent('msalLogger', {
                            detail: authErrorMessage
                        }));
                    }
                };
                fetchData();
            }
        };
        handleClientId();
    }, [activeAccount, instance, navigate, clientId, location.pathname]);

    // Keep accessToken state in sync with localStorage changes
    useEffect(() => {
        const storedToken = localStorage.getItem('accessToken');
        if (storedToken !== accessToken) {
            setAccessToken(storedToken);
        }
    }, [location.pathname, accessToken]);

    useEffect(() => {
        if (!clientId && accessToken && pathname === '/') {
            let redirectTo = userReqURL;
            if (redirectTo === '/') {
                redirectTo = '/mdc/auth/media';
            }
            navigate(redirectTo);
        }
        if (!accessToken) {
            setUserReqURL(pathname);
        }
    }, [accessToken]);

    // Token Refresh Service - Start automatic token refresh when user is authenticated
    useEffect(() => {
        // Start token refresh service when we have an access token
        if (accessToken && accessToken !== '') {
            tokenRefreshService.startTokenRefresh();
        }

        // Listen for token refresh events
        const handleTokenRefreshed = () => {
            // Update state with new token from localStorage
            const newToken = localStorage.getItem('accessToken');
            if (newToken !== accessToken) {
                setAccessToken(newToken);
            }
        };

        window.addEventListener('token-refreshed', handleTokenRefreshed);

        // Cleanup: Stop token refresh service when component unmounts or token is cleared
        return () => {
            tokenRefreshService.stopTokenRefresh();
            window.removeEventListener('token-refreshed', handleTokenRefreshed);
        };
    }, [accessToken]); // Re-run when accessToken changes

    
    return (
        <div>
            <ErrorBoundary>
                <Routes>
                    <Route path='/devmsfhealth' element={<HealthCheck />} />
                    <Route path='/login' element={<Login />} />
                    {(accessToken !== '' && accessToken !== null) ? (
                        <>
                            <Route exact path='/mdc/auth/media/v1/*' element={<MyHelp />} />
                            <Route path='/mdc/auth/media/*' element={<Dashboard />} />
                            <Route path='/mdc/auth/media/*?upload=1' element={<Modal />} />
                            <Route path='/mdc/auth/media/*?bulkupload=1' element={<BulkUploadPopup />} />
                            <Route path='/mdc/auth/media/*?sms_config=1' element={<SmsPopup />} />
                            <Route path='/mdc/auth/media/*?unzip=1' element={<Unzip />} />
                            <Route path='/mdc/auth/media/*?newfolder=1' element={<NewFolder />} />
                            <Route path='/mdc/auth/media/*?rename=1' element={<RenameFolder />} />
                            <Route path='/mdc/preview/*' element={<Preview />} />
                            <Route path='/mdc/download/*' element={<DownloadZip />} />
                            <Route path='/user/list' element={<UserList />} />
                            <Route path='/Admin' element={< Admin/>} />
                            <Route path='/user/list?edituser=1' element={<UserList />} />
                            <Route path='/user/add' element={<UserGroup />} />
                            <Route path='/fileUpload' element={<FileUpload />} />
                            <Route path='/*' element={<Navigate replace to='/mdc/auth/media' />} />
                        </>
                    ) : (
                        <>
                            <Route exact path='/' element={<Login redirectUserTo='/mdc/auth/media' />} />
                            <Route path='/*' element={<Navigate replace to='/' />} />
                        </>
                    )}
                </Routes>
            </ErrorBoundary>
        </div>
    );
}

export default Root;


